import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal, { SweetAlertResult } from 'sweetalert2';

import * as apiSearch from 'api/tagCategory/PostTagCategorySearchV1';
import Search from 'classes/StandingData/TagCategory/Search/Search';
import TagCategory from 'classes/StandingData/TagCategory/Search/TagCategory';
import TagCategoryList from 'classes/StandingData/TagCategory/Search/TagCategoryList';
import TagCategoryDetailComponent from 'components/desktop/StandingData/TagCategory/Detail/TagCategoryDetailComponent';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import StandingDataList from 'components/desktop/_general/StandingDataList/StandingDataList';
import clone from 'functions/clone';
import { QueryParams, getQueryParams } from 'functions/getQueryParams';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	archive?: boolean;
}

const maxNumberOfButtons = 5;

const TagCategoryManagementComponent = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);

	const [tagCategoryList, setTagCategoryList] = useState<TagCategoryList>(new TagCategoryList());
	const [search, setSearch] = useState<Search>(new Search());
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [displayOverlay, setDisplayOverlay] = useState<boolean>(false);

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		if (props.archive !== undefined && props.archive == true) {
			search.showOnlyDeleted = true;
		}
		if (window.location.href.includes('?')) {
			const queryParams: QueryParams = getQueryParams();
			search.mapFromUrl(queryParams);
			if (search.id.value) setDisplayOverlay(true);
			await getTagCategories();
		} else {
			await getTagCategories();
		}
		setSearch(clone(search));
	};

	const getTagCategories = async () => {
		setIsLoading(true);
		try {
			const response: apiSearch.ResponseData | null = await tagCategoryList.callApi(
				search.mapToRequest()
			);
			if (response) {
				tagCategoryList.mapFromApi(response);
				setTagCategoryList(clone(tagCategoryList));
			}
		} finally {
			setIsLoading(false);
		}
	};

	const getSuggestedTagCategories = async (): Promise<ISelectItem[]> => {
		if (searchTerm.length >= 1) {
			await tagCategoryList.search(searchTerm, props.archive ?? false);
		} else {
			tagCategoryList.filtered = [];
		}

		setTagCategoryList(clone(tagCategoryList));
		return tagCategoryList.filtered;
	};

	const handleChangeUrl = () => {
		const url: string | null = search.mapToUrl();
		if (url) navigate(url);
		getTagCategories();
	};

	const handleSearch = (searchTerm?: string) => {
		search.setSearchTerm(searchTerm ?? '');
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const handleChangeId = (id: string | null) => {
		search.id.value = id;
		handleChangeUrl();
	};

	const handleOverlayOpen = (input: string): void => {
		setDisplayOverlay(true);
		handleChangeId(input);
	};

	const handleOverlayClose = (): void => {
		setDisplayOverlay(false);
		handleChangeId(null);
	};

	const handleArchiveTagCategory = async (tagCategory: TagCategory): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:ARCHIVE_TITLE'),
			text: t('standingData:TAG_CATEGORY_ARCHIVE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#ff0004',
			confirmButtonColor: '#ff0004',
			confirmButtonText: t('_general:ARCHIVE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await tagCategory.callApiDelete();
				await Swal.fire({
					title: t('standingData:TAG_CATEGORY_ARCHIVE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getTagCategories();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleRestoreTagCategory = async (tagCategory: TagCategory): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:RESTORE_TITLE'),
			text: t('standingData:TAG_CATEGORY_RESTORE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#00cc07',
			confirmButtonColor: '#00cc07',
			confirmButtonText: t('_general:RESTORE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await tagCategory.callApiRestore();
				await Swal.fire({
					title: t('standingData:TAG_CATEGORY_RESTORE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getTagCategories();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleChangeIdToNew = (): void => {
		handleChangeId('new');
		setDisplayOverlay(true);
	};

	const getButtonDataArchive = (input: TagCategory): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleArchiveTagCategory(input),
			};
		}
	};

	const getButtonDataEdit = (id: string): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleOverlayOpen(id),
			};
		}
	};

	const getButtonDataRestore = (input: TagCategory): BtnData | undefined => {
		if (props.archive) {
			return {
				onClick: () => handleRestoreTagCategory(input),
			};
		}
	};

	const renderButtons = (input: TagCategory): JSX.Element => {
		return (
			<BtnGroup
				edit={getButtonDataEdit(input.id)}
				archive={getButtonDataArchive(input)}
				restore={getButtonDataRestore(input)}
			/>
		);
	};

	const renderListRows = () => {
		return tagCategoryList.all.map((tagCategory: TagCategory) => {
			return {
				id: tagCategory.id,
				jsx: (
					<div
						className="d-flex flex-row justify-content-start align-items-center"
						style={{ gap: '2em' }}
					>
						<div className="flex-fill">{tagCategory.name}</div>
						<div>{renderButtons(tagCategory)}</div>
					</div>
				),
			};
		});
	};

	const renderContent = () => {
		return (
			<>
				<main className="container">
					<div className="d-flex flex-row justify-content-between">
						{!props.archive ? (
							<>
								<h1>{t('standingData:TAG_CATEGORY_MANAGEMENT')}</h1>
								<div className="d-flex flex-gap-25">
									<BtnNew
										onClick={handleChangeIdToNew}
										name={t('standingData:CREATE_TAG_CATEGORY')}
									/>
								</div>
							</>
						) : (
							<h1>{t('standingData:TAG_CATEGORY_ARCHIVE')}</h1>
						)}
					</div>
					<br />
					<div className="row">
						<div className="col-lg-12">
							<div className="input-group mb-3">
								<AdvancedSearchInput
									debounceMilliSeconds={350}
									getSuggestedItems={getSuggestedTagCategories}
									handleSearch={handleSearch}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									triggerSearchLetterAmount={1}
								/>
							</div>
						</div>
					</div>
					<br />
					<LoadingAnimation isLoading={isLoading} />
					<p>{tagCategoryList.totalCount + ' ' + t('_general:TAG_CATEGORIES')}</p>
					<StandingDataList
						archive={props.archive}
						displayOverlay={displayOverlay}
						rows={renderListRows()}
						overlay={<TagCategoryDetailComponent />}
						handleOverlayOpen={handleOverlayOpen}
						handleOverlayClose={handleOverlayClose}
					/>
					<br />
					<Pagination
						countOfAllListItems={tagCategoryList.totalCount}
						pageIndex={search.getPageIndex()}
						setPageIndex={handleChangePageIndex}
						listItemAmount={search.getPageSize()}
						maxNumberOfButtons={maxNumberOfButtons}
					/>
				</main>
			</>
		);
	};

	return <>{renderContent()}</>;
};

export default TagCategoryManagementComponent;
